import z from "zod";
import ZodSchemaUtil from "util/schema-helpers/ZodSchemaUtil";

// PowurOS serializes full-precision decimals as JSON strings (e.g. "2.4106636212624584717607973422"
// for per-watt values on Powurline projects); normalize them to numbers so validation doesn't fail.
const PerWattNumberSchema = z.preprocess(
  (value) => (typeof value === "string" ? Number(value) : value),
  z.number().nullish(),
);

export const EquipmentTypeSchema = z.union([
  z.literal("AUTOTRANSFORMER"),
  z.literal("BATTERY_COMPONENTS"),
  z.literal("CABLES"),
  z.literal("COMBINER"),
  z.literal("ENERGY_BACKUP"),
  z.literal("EV_CHARGER"),
  z.literal("EV_CHARGER_COMPONENTS"),
  z.literal("INVERTER"),
  z.literal("INVERTER_COMPONENTS"),
  z.literal("MISCELLANEOUS_ELECTRICAL"),
  z.literal("MODEM"),
  z.literal("MODULE"),
  z.literal("OPTIMIZER"),
  z.literal("RACKING"),
  z.literal("SMART_PANEL"),
  z.literal("SMART_PANEL_COMPONENT"),
  z.literal("TERMINATOR_CAP"),
]);

export const EquipmentSchema = z.object({
  name: z.string(),
  type: z.union([EquipmentTypeSchema, z.string()]),
  qty: z.number(),
  typeId: z.number(),
  price: z.number().nullable(),
});

export const AdderSchema = z.object({
  name: z.string(),
  qty: z.number(),
  cost: z.number(),
  type: z.string(),
});

export const RebateSchema = z.object({
  name: z.string(),
  paymentDate: z.string().nullable(),
  amount: z.number(),
});

export const DiscountSchema = z.object({
  name: z.string(),
  amount: z.number(),
});

export const IncentiveSchema = z.object({
  name: z.string(),
  amount: z.number(),
});

const ChangeOrderAllowedTypesSchema = z.discriminatedUnion("name", [
  z.object({
    id: z.literal(1),
    name: z.literal("Absorb"),
  }),
  z.object({
    id: z.literal(2),
    name: z.literal("Shade Waiver"),
  }),
  z.object({
    id: z.literal(3),
    name: z.literal("Addendum"),
  }),
  z.object({
    id: z.literal(4),
    name: z.literal("Change Order"),
  }),
  z.object({
    id: z.literal(5),
    name: z.literal("SOW Confirmation"),
  }),
  z.object({
    id: z.literal(6),
    name: z.literal("ICO Only"),
  }),
  z.object({
    id: z.literal(7),
    name: z.literal("Base Costs"),
  }),
]);

const ChangeOrderDateSchema = z.object({
  dateType: z.string(),
  completeDate: z.nullable(z.string()),
  approvalType: z.string().nullish(),
});

export const ChangeOrderSchema = z.object({
  allowedTypes: z.array(ChangeOrderAllowedTypesSchema),
  reasons: z.array(z.string()),
  designNotes: z.string(),
  changeOrderDates: z.array(ChangeOrderDateSchema),
  changeOrderType: z.nullable(z.string()),
  changeOrderTypeId: z.nullable(z.number()),
  versionCreatedDate: z.string(),
  versionCreatedBy: z.string(),
  changeOrderWorkFlowTypeName: z.string(),
  changeOrderWorkFlowType: z.number(),
});

export const FundingTypeDetailsSchema = z.object({
  m3EndedAt: z.string().nullable(),
  maxAdvance: z.number(),
  fundingType: z.string(),
  m3StartedAt: z.string().nullable(),
  fundingTypeId: z.number(),
  rebateActiveAt: z.string().nullable(),
  rebatePercentage: z.number(),
  rebateDeprecatedAt: z.string().nullable(),
});

export const ContractSchema = z.object({
  contractId: z.number(),
  changeOrder: ChangeOrderSchema.nullish(),
  fundingType: z.string(),
  systemSize: z.number(),
  equipment: z.array(EquipmentSchema),
  adders: z.array(AdderSchema),
  finalizedDate: z.string().nullable(),
  dateSigned: z.string(),
  totalContractPrice: z.number(),
  cashPayment: z.number(),
  totalFinanced: z.number(),
  loanTerm: z.number(),
  loanApr: z.number(),
  dealerFeePercent: z.number(),
  dealerFee: z.number(),
  estimatedAnnualProduction: z.number(),
  promotionalPayment: z.number().nullable(),
  federalTaxCredit: z.number(),
  secondPaymentWithIncentives: z.number(),
  secondPaymentWithoutIncentives: z.number(),
  cosignerName: z.nullable(z.string()),
  cosignerEmail: z.nullable(z.string()),
  cosignerFirstName: z.nullable(z.string()),
  cosignerlastName: z.nullable(z.string()),
  fundingTypeExcludeFromHso: z.boolean(),
  salesTax: z.number(),
  isTpo: z.boolean().nullable(),
  contractCosts: z.number(),
  estimatedNetRevenue: z.number(),
  rebates: z.array(RebateSchema),
  discounts: z.array(DiscountSchema).nullish(),
  incentives: z.array(IncentiveSchema).nullish(),
  additionalCosts: z.array(z.object({ cost: z.number(), name: z.string() })),
  fundingTypeDisclosure: z.string(),
  baseCost: z.number().nullish(),
  addersPrice: z.number().nullish(),
  salesMargin: z.number().nullish(),
  exciseTax: z.number().nullish(),
  deletedDateTime: z.string().nullable(),
  powurLineMargin: z.number().nullish(),
  salesTaxPerWatt: PerWattNumberSchema,
  exciseTaxPerWatt: PerWattNumberSchema,
  loanProviderOwner: z.string().nullish(),
  addersPricePerWatt: PerWattNumberSchema,
  fundingTypeDetails: FundingTypeDetailsSchema.nullish(),
  salesMarginPerWatt: PerWattNumberSchema,
  enterpriseCommission: z.number().nullish(),
  leadershipCommission: z.number().nullish(),
  minimumContractPrice: z.number().nullish(),
  sellerPowurLinePrice: z.number().nullish(),
  powurLineMarginPerWatt: PerWattNumberSchema,
  completedChangeOrderType: z.string().nullish(),
  enterprisePowurLinePrice: z.number().nullish(),
  baseCostPerWatt: PerWattNumberSchema,
  totalContractPricePerWatt: PerWattNumberSchema,
  completedChangeOrderTypeId: z.number().nullish(),
  enterpriseCommissionPerWatt: PerWattNumberSchema,
  leadershipCommissionPerWatt: PerWattNumberSchema,
  minimumContractPricePerWatt: PerWattNumberSchema,
  sellerPowurLinePricePerWatt: PerWattNumberSchema,
  enterprisePowurLinePricePerWatt: PerWattNumberSchema,
});

export const DraftContractSchema = ZodSchemaUtil.makePropertiesNullable<typeof ContractSchema>(ContractSchema, [
  "contractId",
  "totalContractPrice",
  "changeOrder",
]);

const CommissionSheetItemSchema = z.object({
  id: z.number().nullish(),
  cost: z.number(),
  name: z.string(),
  sort: z.number().nullish(),
  type: z.number().nullish(),
});

export const CommissionSheetSchema = z.object({
  id: z.string(),
  approvalDate: z.string().nullish(),
  historyOfRevenue: z
    .array(
      z.object({
        revenue: z.number(),
        createdDateTime: z.string(),
        availableCommission: z.number(),
        recalcTriggeredFrom: z.string().nullish(),
      }),
    )
    .nullish(),
  issues: z.array(z.string()).nullish(),
  lineItems: z.array(CommissionSheetItemSchema),
  type: z.union([z.literal("M2"), z.literal("M1"), z.literal("M1.5")]).nullish(),
  updatedAt: z.string().nullish(),
});

export const DataTotalItemSchema = z.object({ name: z.string(), total: z.number() });

const AddersDataSchema = z.object({
  latestFinalizedContractAdders: z.array(DataTotalItemSchema),
  changeOrderAdders: z.array(z.object({ name: z.string(), total: z.number() })).nullish(),
  contractsAdders: z.array(
    z.object({
      contractId: z.number(),
      addersData: z.array(DataTotalItemSchema),
    }),
  ),
});

const SystemCostsListSchema = ZodSchemaUtil.makeStringLiteralUnion([
  "POWURLINE_PRICE",
  "MODULE",
  "INVERTER",
  "RACKING",
  "OPTIMIZER",
  "MODEM",
  "ENERGY_BACKUP",
  "BALANCE_OF_SYSTEMS",
]);

export const SystemCostItemSchema = z.object({
  type: SystemCostsListSchema,
  name: z.string().nullable(),
  total: z.number().nullable(),
  label: z.string(),
  qty: z.number().nullish(),
  costSuppressed: z.boolean().nullish(),
});

const SystemCostsDataSchema = z.object({
  latestFinalizedContractSystemCosts: z.array(SystemCostItemSchema),
  systemCostsList: z.array(SystemCostsListSchema),
  contractsSystemCosts: z.array(
    z.object({
      contractId: z.number(),
      systemCosts: z.array(SystemCostItemSchema),
    }),
  ),
  changeOrderSystemCosts: z.array(SystemCostItemSchema).nullish(),
});

const CoreServicesDataSchema = z.object({
  latestFinalizedContractServices: z.array(DataTotalItemSchema),
  changeOrderServices: z.array(DataTotalItemSchema).nullish(),
  contractsCoreServices: z.array(
    z.object({
      contractId: z.number(),
      addersData: z.array(DataTotalItemSchema),
    }),
  ),
});

const TotalItemSchema = z.object({
  adders: z.number(),
  systemCosts: z.number(),
});

const TotalDataSchema = z.object({
  latestFinalizedTotals: TotalItemSchema,
  changeOrderTotals: TotalItemSchema.nullish(),
  contractsSystemCosts: z.array(z.object({ contractId: z.number(), total: z.number() })),
  contractsAdders: z.array(z.object({ contractId: z.number(), total: z.number() })),
});

const WorkFlowSlug = ZodSchemaUtil.makeStringLiteralUnion([
  "change_order",
  "sow",
  "admin",
  "buildable_as_sold",
  "buildable_with_changes",
  "change_order_required",
  "undetermined",
  "late_stage_change_order_required",
]);

export const ContractsResponseBodySchema = z.object({
  contracts: z.array(ContractSchema),
  submissionList: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
        createdDateTime: z.string().nullable(),
      }),
    )
    .nullish(),
  commissionSheets: z.array(CommissionSheetSchema),
  commissionSheetOriginal: CommissionSheetSchema,
  changeOrder: ChangeOrderSchema.nullable(),
  draftContract: DraftContractSchema.nullable(),
  designLayoutUrl: z.string().nullable(),
  designLayoutRequiresApproval: z.boolean().nullish(),
  firstFinalizedContract: ContractSchema,
  latestFinalizedContract: ContractSchema,
  m1ApprovedContract: ContractSchema.nullable(),
  estimatedNetRevenue: z.number().nullable(),
  pendingDesignChange: z.boolean(),
  pendingDesignChangeCreatedAt: z.string().nullable(),
  addersData: AddersDataSchema,
  coreServicesData: CoreServicesDataSchema,
  roofTypeAsDesigned: z.string().nullable(),
  roofTypeAsSold: z.string().nullable(),
  systemCostsData: SystemCostsDataSchema,
  contractUpdates: z.record(z.array(z.string())).nullable(),
  salesConsultantInformed: z.boolean(),
  salesConsultantInformedAt: z.string().nullable(),
  totals: TotalDataSchema,
  workFlowSlug: WorkFlowSlug.nullish(),
  changeTeamHuddleLink: z.string().nullable(),
  isRevisePriceBlacklistState: z.boolean(),
  isApproved: z.boolean().nullish(),
  approvedOn: z.string().nullish(),
  approvedBy: z.string().nullish(),
  homeownerAcceptedAt: z.string().nullish(),
  changeSpecialistAcceptedAt: z.string().nullish(),
  seeContractComparison: z.boolean(),
  isPowurline: z.boolean(),
  providerLeadId: z.number().optional(),
});

export const ProjectContractsResponse = z
  .object({
    body: ContractsResponseBodySchema,
  })
  .nullable();
